// Process Overview — the app-level door.
//
// Diagram → data → trail, on one screen. It answers "what does this business
// do" before it answers anything about a record: pick a process, read it, step
// into any node's records (as the SAME feed cards a trail draws, so a purchase
// order reads identically in both places), and from a record open its trail.
//
// Mounted wherever an application defines at least one Process Flow — the
// platform Run view and a downloaded copy of the app both render this
// component, so neither can drift from the other.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowLeft, Plus, RefreshCcw } from 'lucide-react'

import type { TransactionModel } from '../types'
import { openRecord, parseApiError } from '../utils'
import type { RowAction } from '../list/RowActionsMenu'
import PaginationBar, { type ListPagination } from '../list/PaginationBar'
import FeedCard from './FeedCard'
import FlowDiagram, { FlowFilter } from './FlowDiagram'
import ProcessTrailModal from './ProcessTrailModal'
import { buildRecordActions } from './recordActions'
import { processFlows, processNodeData } from './contract'
import { EDGE_STYLE } from './shapes'
import type { FlowNode, FlowSummary, NodeActions, NodeDataResponse } from './contract'

// Where NodeActions used to live. It is part of the wire-adjacent contract now
// (the trail offers the same menu), and re-exported here so hosts that import
// it from this module keep working.
export type { NodeActions }

export interface OverviewRuntime {
  execute(objName: string, action: string, payload?: Record<string, unknown>): Promise<unknown>
  load(objName: string, txnId: string): Promise<{ header?: Record<string, unknown> }>
  getModel?(objName: string): Promise<TransactionModel>
}

interface Props {
  project: string
  runtime: OverviewRuntime
  /** Take the viewer to a record on its own screen. */
  onOpenRecord?: (objName: string, txnId: string) => void
  /** Open on the process this object sits in, with its own step already
   *  showing. Set when the view is opened FROM that object's screen — the
   *  person is asking "where does this fit", not "what processes exist". */
  focusObj?: string
  /** The host's own record actions, for its own object. See NodeActions. */
  nodeActions?: NodeActions
  /** Bumped by the host whenever it re-reads its records, so a save made in the
   *  record editor over this view refreshes the cards underneath it. */
  reloadKey?: number
  /** Controls for the right of this screen's own header — the transaction
   *  screen threads its view switcher here so the process view carries ONE
   *  toolbar, the way the list and spreadsheet views do. */
  headerRight?: React.ReactNode
}

/** Rows per page the rail opens on — the same default the object's own list
 *  uses, so switching to Process does not quietly change how much you see. */
const NODE_PAGE_SIZE = 50

/** Under this much room for the pair, the two panes stack into one column. A
 *  460px diagram beside a 260px rail is the narrowest honest two-column split;
 *  below it the rail was being squeezed to a word wide. */
const STACK_BELOW = 720

export default function ProcessOverview({
  project, runtime, onOpenRecord, focusObj, headerRight, nodeActions, reloadKey,
}: Props) {
  const [flows, setFlows] = useState<FlowSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  const [node, setNode] = useState<FlowNode | null>(null)
  const [data, setData] = useState<NodeDataResponse | null>(null)
  const [busy, setBusy] = useState(false)
  // A step whose records could not be read says so IN THE PANEL. The screen's
  // top-level `error` is only drawn before the flows arrive, so a failing node
  // used to leave an empty rail and no reason for it — which reads as "this
  // step has nothing", the one thing it must not say.
  const [nodeError, setNodeError] = useState<string | null>(null)
  const [trailFor, setTrailFor] = useState<{ obj: string; txnId: string } | null>(null)
  // Which node the panel is on, readable from a callback that must not be
  // rebuilt every time the selection changes.
  const nodeRef = useRef<FlowNode | null>(null)

  // The rail pages like the object's own list, through the SAME footer.
  //
  // It used to ask for 20 records and say "Showing the 20 most recent. Open the
  // object for the full list." — which is a screen telling you to go and look
  // somewhere else. A step of a process is the object's records; the panel
  // therefore takes limit/offset (the list action's own) and draws
  // <PaginationBar>, so rows-per-page, Continuous ⇆ Pages and Prev/Next mean
  // here exactly what they mean in the grid.
  const [pageMode, setPageMode] = useState<'infinite' | 'paged'>('infinite')
  const [pageSize, setPageSize] = useState(NODE_PAGE_SIZE)
  const [page, setPage] = useState(0)
  const [loadingMore, setLoadingMore] = useState(false)
  // Read from callbacks that must not be rebuilt on every page change.
  const pageSizeRef = useRef(NODE_PAGE_SIZE)
  const loadedRef = useRef(0)
  useEffect(() => { loadedRef.current = data?.cards.length ?? 0 }, [data])

  // Diagram beside records, or diagram above them — measured, not guessed.
  //
  // This used to be one `flex-wrap: wrap` row, which is why the records rail
  // would not scroll. A WRAPPING flex container sizes each line to its own
  // content, so both panes were as tall as whatever they held: their
  // `overflow: auto` had nothing to scroll, and the rail's cards ran off the
  // bottom of a screen that clips. Side by side the row must therefore NOT
  // wrap — then each pane is the container's height and scrolls itself — and
  // when there is no room for two columns the whole view becomes one scrolling
  // page, which is the readable answer on a phone anyway.
  const [bodyEl, setBodyEl] = useState<HTMLDivElement | null>(null)
  const [stacked, setStacked] = useState(false)

  useEffect(() => {
    if (!bodyEl) return undefined
    const measure = () => {
      const w = bodyEl.clientWidth
      // Width 0 = not laid out yet (a hidden tab); keep the last verdict.
      if (w > 0) setStacked(w < STACK_BELOW)
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return undefined
    const ro = new ResizeObserver(measure)
    ro.observe(bodyEl)
    return () => ro.disconnect()
  }, [bodyEl])

  /** One page of a node's records. `append` adds the next page below the ones
   *  already drawn (Continuous); otherwise it REPLACES them, which is both a
   *  refresh and how Pages mode moves. A failed append keeps the cards already
   *  on screen — losing them to say "that didn't load" helps nobody. */
  const fetchCards = useCallback(async (
    n: FlowNode, offset: number, append: boolean,
  ) => {
    if (!n.obj_name) return
    if (append) setLoadingMore(true)
    else setBusy(true)
    try {
      const res = await processNodeData(
        runtime.execute, n.obj_name, pageSizeRef.current, offset)
      setNodeError(null)
      setData((prev) => (append && prev
        ? { ...res, cards: [...prev.cards, ...res.cards] }
        : res))
    } catch (e) {
      setNodeError(parseApiError(e).message)
      if (!append) setData(null)
    } finally {
      if (append) setLoadingMore(false)
      else setBusy(false)
    }
  }, [runtime])

  /** Continuous mode: the next page, appended. */
  const loadMore = useCallback(async () => {
    const n = nodeRef.current
    if (n) await fetchCards(n, loadedRef.current, true)
  }, [fetchCards])

  /** Pages mode: jump to a 0-based page, replacing what is drawn. */
  const gotoPage = useCallback(async (next: number) => {
    const n = nodeRef.current
    if (!n) return
    const target = Math.max(0, next)
    setPage(target)
    await fetchCards(n, target * pageSizeRef.current, false)
  }, [fetchCards])

  /** Rows per page changed — start again from the first one. */
  const changePageSize = useCallback(async (size: number) => {
    pageSizeRef.current = Math.max(1, size)
    setPageSize(pageSizeRef.current)
    setPage(0)
    const n = nodeRef.current
    if (n) await fetchCards(n, 0, false)
  }, [fetchCards])

  /** Continuous ⇆ Pages. Both restart at the first page so the offset
   *  bookkeeping begins clean, exactly as the list's own toggle does. */
  const changeMode = useCallback(async (next: 'infinite' | 'paged') => {
    setPageMode(next)
    setPage(0)
    const n = nodeRef.current
    if (n) await fetchCards(n, 0, false)
  }, [fetchCards])

  /**
   * Step into a box.
   *
   * A step, an entity and a data store are all ordinary objects, so all three
   * list their own records — which is precisely why a store is drawn: the data
   * the process changed has a screen, and this is it. The two boxes that are
   * NOT objects get the right question instead: another PROCESS switches the
   * diagram to that process, and an outside party has nothing to open.
   */
  const loadNode = useCallback(async (n: FlowNode) => {
    if (n.kind === 'process' && n.flow) { setFilter(n.flow); return }
    setNode(n)
    nodeRef.current = n
    setData(null)
    setNodeError(null)
    // A different step is a different set of records: back to its first page.
    loadedRef.current = 0
    setPage(0)
    if (n.kind === 'external' || !n.obj_name) {
      setNodeError('There is nothing to open here.')
      return
    }
    await fetchCards(n, 0, false)
  }, [fetchCards])

  // An action or a delete changes the record the panel is showing, so the node
  // is re-read once the host's handler settles — the list views refresh
  // themselves for the same reason.
  const runAction = useCallback(async (
    acts: NodeActions, name: string, txnId: string,
  ) => {
    try {
      await acts.onRunRowAction?.(name, txnId)
    } finally {
      if (nodeRef.current) await loadNode(nodeRef.current)
    }
  }, [loadNode])

  const runDelete = useCallback(async (acts: NodeActions, txnId: string) => {
    try {
      await acts.onDelete?.(txnId)
    } finally {
      if (nodeRef.current) await loadNode(nodeRef.current)
    }
  }, [loadNode])

  /** Clicking a card OPENS THE RECORD, the way clicking a list row does — on
   *  this screen when the node is the host's own object, on that object's own
   *  screen otherwise. The trail is a deliberate choice, offered in the kebab,
   *  not what a click lands on. It stays the click only where neither way of
   *  opening the record exists. */
  const openCard = useCallback((
    n: FlowNode,
    entry: NodeDataResponse['cards'][number],
  ) => {
    const obj = n.obj_name
    const mine = nodeActions && nodeActions.objName === obj ? nodeActions : null
    if (mine?.onEdit && mine.canEdit !== false) {
      // Through the object's edit / view system action, so a click here is
      // gated by the same conditions as a click on its list row.
      openRecord({
        txnId: entry.txn_id,
        editable: entry.editable,
        systemOverrides: mine.systemOverrides,
        onRunRowAction: mine.onRunRowAction,
        onOpen: (id) => mine.onEdit!(id),
      })
      return
    }
    if (onOpenRecord) { onOpenRecord(obj, entry.txn_id); return }
    setTrailFor({ obj, txnId: entry.txn_id })
  }, [nodeActions, onOpenRecord])

  /** The kebab for one card — the SAME builder the trail uses, so a record
   *  offers the same things in the same order wherever it is shown. */
  const cardActions = useCallback((
    n: FlowNode,
    entry: NodeDataResponse['cards'][number],
  ): RowAction[] => {
    const obj = n.obj_name
    const mine = nodeActions && nodeActions.objName === obj ? nodeActions : null
    return buildRecordActions({
      txnId: entry.txn_id,
      editable: entry.editable,
      hiddenActions: entry.hidden_actions,
      own: mine,
      onRun: (name) => { void runAction(mine!, name, entry.txn_id) },
      onDelete: () => { void runDelete(mine!, entry.txn_id) },
      onProcessTrail: () => setTrailFor({ obj, txnId: entry.txn_id }),
      onOpenFullScreen: onOpenRecord
        ? () => onOpenRecord(obj, entry.txn_id)
        : undefined,
    })
  }, [nodeActions, onOpenRecord, runAction, runDelete])

  useEffect(() => {
    let cancelled = false
    processFlows(runtime.execute, focusObj || '')
      .then((res) => {
        if (cancelled) return
        setFlows(res.flows || [])
        if (!focusObj) return
        // Opened from an object's own screen: show the process it is in, with
        // its step selected, rather than every process in the application.
        const mine = (res.in_flows || [])[0]
        if (mine) setFilter(mine)
        const node = (res.flows || [])
          .flatMap((f) => f.nodes || [])
          .find((n) => n.obj_name === focusObj)
        if (node) void loadNode(node)
      })
      .catch((e) => { if (!cancelled) setError(parseApiError(e).message) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runtime, focusObj])

  // The host re-read its records — a save, an action, a delete. Re-read the
  // node's cards so the panel is not left showing what the record used to say.
  useEffect(() => {
    if (reloadKey === undefined || !nodeRef.current) return
    void loadNode(nodeRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey])

  const shown = useMemo(
    () => (flows || []).filter((f) => !filter || f.name === filter),
    [flows, filter])

  /** What the rail's footer is handed — the SAME shape the grid's footer takes,
   *  so the two are the same control over the same kind of page. Null while
   *  there is nothing paginable: no step chosen, or one the viewer may not
   *  read. */
  const nodePagination: ListPagination | null = useMemo(() => {
    if (!data || data.restricted) return null
    return {
      loadedCount: data.cards.length,
      total: data.total,
      hasMore: data.cards.length < data.total,
      loadingMore,
      onLoadEarlier: loadMore,
      mode: pageMode,
      onSetMode: changeMode,
      pageSize,
      onSetPageSize: changePageSize,
      page,
      onGotoPage: gotoPage,
    }
  }, [data, loadingMore, loadMore, pageMode, changeMode, pageSize,
    changePageSize, page, gotoPage])

  if (error && !flows) {
    return <div style={{ padding: 20, color: 'var(--color-error, #dc2626)' }}>{error}</div>
  }
  if (!flows) {
    return <div style={{ padding: 20, color: 'var(--color-text-secondary)' }}>Loading processes…</div>
  }
  if (flows.length === 0) {
    return (
      <div style={{ padding: 24, color: 'var(--color-text-secondary)', fontSize: 14.5 }}>
        This application doesn’t define a process flow yet. Draw one in the
        Process Flow designer and it appears here.
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        padding: '9px 13px', borderBottom: '1px solid var(--color-border)',
        background: 'var(--color-bg-secondary, #fff)',
      }}>
        <strong style={{ fontSize: 15 }}>
          {focusObj ? 'Process' : 'Process overview'}
        </strong>
        <FlowFilter flows={flows} value={filter} onChange={setFilter} />
        <span style={{ flex: 1 }} />
        {node && (
          <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
            Showing {node.label}
          </span>
        )}
        {headerRight}
      </div>

      <div
        ref={setBodyEl}
        style={{
          flex: 1, minHeight: 0, display: 'flex',
          flexDirection: stacked ? 'column' : 'row',
          // Stacked, the page is the scroller. Side by side, each pane is.
          overflowY: stacked ? 'auto' : 'hidden',
          overflowX: 'hidden',
        }}
      >
        <div style={{
          flex: stacked ? '0 0 auto' : '1 1 460px',
          minWidth: 0, minHeight: 0,
          overflow: stacked ? 'visible' : 'auto',
          padding: 14,
          ...(stacked
            ? { borderBottom: '1px solid var(--color-border)' }
            : { borderRight: '1px solid var(--color-border)' }),
        }}>
          {shown.map((f) => (
            <div key={f.name} style={{ marginBottom: 22 }}>
              {shown.length > 1 && (
                <div style={{
                  fontSize: 13, letterSpacing: '0.1em', textTransform: 'uppercase',
                  color: 'var(--color-text-secondary)', marginBottom: 6,
                }}>
                  {f.title}
                </div>
              )}
              <FlowDiagram flow={f} activeObj={node?.obj_name} onSelectNode={loadNode} />
            </div>
          ))}
          <Legend />
        </div>

        <div style={{
          flex: stacked ? '0 0 auto' : '0 1 340px',
          minWidth: stacked ? 0 : 260, minHeight: 0,
          display: 'flex', flexDirection: 'column',
          background: 'var(--color-bg-secondary, #fff)',
        }}>
        {/* The cards scroll; the pagination footer below them does not. */}
        <div style={{
          flex: stacked ? '0 0 auto' : 1, minHeight: 0,
          overflow: stacked ? 'visible' : 'auto',
          padding: 12,
        }}>
          {!node && (
            <div style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>
              Choose a step on the diagram to see its records.
            </div>
          )}
          {node && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{data?.title || node.label}</div>
                  <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                    {node.obj_name}
                  </div>
                </div>
                <span style={{ flex: 1 }} />
                {nodeActions && nodeActions.objName === node.obj_name
                  && nodeActions.onAdd && nodeActions.canAdd !== false && (
                  <button className="btn btn-ghost btn-sm" onClick={() => nodeActions.onAdd!()}
                    aria-label={`New ${data?.title || node.label}`}
                    title={`New ${data?.title || node.label}`}>
                    <Plus size={13} />
                  </button>
                )}
                <button className="btn btn-ghost btn-sm" onClick={() => loadNode(node)}
                  aria-label="Refresh">
                  <RefreshCcw size={13} />
                </button>
              </div>

              {busy && <div style={{ fontSize: 13.5, color: 'var(--color-text-secondary)' }}>Loading…</div>}
              {nodeError && (
                <div style={{ fontSize: 13.5, color: 'var(--color-error, #dc2626)' }}>
                  {nodeError}
                </div>
              )}
              {data?.restricted && (
                <div style={{ fontSize: 13.5, color: 'var(--color-warning, #b45309)' }}>
                  You don’t have access to this step’s records.
                </div>
              )}
              {data && !data.restricted && data.cards.length === 0 && !busy && (
                <div style={{ fontSize: 13.5, color: 'var(--color-text-secondary)' }}>
                  No records yet.
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {(data?.cards || []).map((c, i) => (
                  <FeedCard
                    key={c.txn_id || `row-${i}`}
                    card={c.card}
                    title={data?.title || node.label}
                    // A row whose key the engine could not resolve has nothing
                    // to open and nothing to act on: it reads, and does no more
                    // than that, rather than sending an empty id to the server.
                    onOpen={c.txn_id ? () => openCard(node, c) : undefined}
                    actions={c.txn_id ? cardActions(node, c) : []}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {nodePagination && (
          <PaginationBar
            pagination={nodePagination}
            busy={busy}
            hasRows={(data?.cards.length || 0) > 0}
          />
        )}
        </div>
      </div>

      {trailFor && (
        <ProcessTrailModal
          project={project}
          objName={trailFor.obj}
          txnId={trailFor.txnId}
          runtime={runtime}
          onClose={() => setTrailFor(null)}
          onOpenFullScreen={onOpenRecord}
          nodeActions={nodeActions}
          onChanged={() => { if (nodeRef.current) void loadNode(nodeRef.current) }}
        />
      )}
    </div>
  )
}

/** The four arrows a designer draws, in the order the process reads them.
 *  Their colours and dashes come from the ONE palette the diagram is drawn
 *  with, so a legend can never describe a picture nobody is looking at. */
const LEGEND_KINDS = ['work', 'link', 'update', 'read']

function Legend() {
  const item = (stroke: string, dash: string | undefined, label: string) => (
    <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{
        width: 16, borderTop: `2px ${dash ? 'dashed' : 'solid'} ${stroke}`, display: 'inline-block',
      }} />
      {label}
    </span>
  )
  return (
    <div style={{
      display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13,
      color: 'var(--color-text-secondary)', paddingTop: 8,
    }}>
      {LEGEND_KINDS.map((k) => item(EDGE_STYLE[k].stroke, EDGE_STYLE[k].dash, EDGE_STYLE[k].label))}
      {item('var(--color-warning, #b45309)', '4 3', 'No access')}
    </div>
  )
}

/** Back-to-overview affordance for hosts that push this as a page. */
export function OverviewBack({ onBack }: { onBack: () => void }) {
  return (
    <button className="btn btn-ghost btn-sm" onClick={onBack}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <ArrowLeft size={13} /> Back
    </button>
  )
}
